import { useState } from 'react'
import { SystemParameter, DepositBonusTier } from '@/types'
import '../styles/TabStyles.css'

interface DepositBonusTabProps {
  parameters: SystemParameter[]
  onUpdate: (key: string, value: string) => Promise<void>
}

export default function DepositBonusTab({ parameters, onUpdate }: DepositBonusTabProps) {
  const [tiers, setTiers] = useState<DepositBonusTier[]>([])
  const [showModal, setShowModal] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [formData, setFormData] = useState<DepositBonusTier>({ deposit_amount: 0, bonus_amount: 0 })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load tiers from parameters
  const loadTiers = () => {
    const param = parameters.find(p => p.param_key === 'deposit_bonus_tiers')
    if (param) {
      try {
        const parsed = JSON.parse(param.param_value)
        setTiers(Array.isArray(parsed) ? parsed : [])
      } catch (err) {
        setError('解析充值赠金档位失败')
      }
    }
  }

  // Load tiers on mount
  if (tiers.length === 0 && parameters.length > 0) {
    loadTiers()
  }

  const handleAddTier = () => {
    setEditingIndex(null)
    setFormData({ deposit_amount: 0, bonus_amount: 0 })
    setShowModal(true)
  }

  const handleEditTier = (index: number) => {
    setEditingIndex(index)
    setFormData(tiers[index])
    setShowModal(true)
  }

  const handleDeleteTier = async (index: number) => {
    if (!confirm('确定要删除这个档位吗？')) return

    const newTiers = tiers.filter((_, i) => i !== index)
    setLoading(true)
    try {
      await onUpdate('deposit_bonus_tiers', JSON.stringify(newTiers))
      setTiers(newTiers)
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveTier = async () => {
    // Validation
    if (formData.deposit_amount <= 0 || formData.bonus_amount <= 0) {
      setError('金额必须大于0')
      return
    }

    let newTiers = [...tiers]
    if (editingIndex !== null) {
      newTiers[editingIndex] = formData
    } else {
      newTiers.push(formData)
    }

    // Sort by deposit_amount
    newTiers.sort((a, b) => a.deposit_amount - b.deposit_amount)

    // Check for duplicates
    const amounts = newTiers.map(t => t.deposit_amount)
    if (new Set(amounts).size !== amounts.length) {
      setError('充值金额不能重复')
      return
    }

    setLoading(true)
    try {
      await onUpdate('deposit_bonus_tiers', JSON.stringify(newTiers))
      setTiers(newTiers)
      setShowModal(false)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="tab-panel">
      <div className="panel-header">
        <h3>充值赠金档位</h3>
        <button className="btn-primary" onClick={handleAddTier}>
          + 添加档位
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {tiers.length === 0 ? (
        <div className="empty-state">
          <p>暂无充值赠金档位</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>充值金额</th>
                <th>赠金金额</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier, index) => (
                <tr key={index}>
                  <td>¥{tier.deposit_amount.toFixed(2)}</td>
                  <td>¥{tier.bonus_amount.toFixed(2)}</td>
                  <td className="action-cell">
                    <button
                      className="btn-small btn-edit"
                      onClick={() => handleEditTier(index)}
                    >
                      编辑
                    </button>
                    <button
                      className="btn-small btn-delete"
                      onClick={() => handleDeleteTier(index)}
                      disabled={loading}
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h4>{editingIndex !== null ? '编辑档位' : '添加档位'}</h4>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>充值金额 (¥)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.deposit_amount}
                  onChange={e => setFormData({ ...formData, deposit_amount: parseFloat(e.target.value) })}
                  placeholder="输入充值金额"
                />
              </div>

              <div className="form-group">
                <label>赠金金额 (¥)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.bonus_amount}
                  onChange={e => setFormData({ ...formData, bonus_amount: parseFloat(e.target.value) })}
                  placeholder="输入赠金金额"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>
                取消
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveTier}
                disabled={loading}
              >
                {loading ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
